import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate, useParams } from 'react-router-dom';
import { FirebaseService } from '../services/firebaseService';

interface MaterielElectriqueSubcategoryProps {
  t: any;
  isRTL: boolean;
}

interface DatabaseProduct {
  id: string;
  name: string;
  title?: string;
  mainImage?: string;
  images?: string[];
  category: string;
  subcategory?: string;
  description?: string;
  characteristics?: any;
  createdAt?: any;
  [key: string]: any;
}

// Styled Components
const CategoryPage = styled.div<{ $isRTL: boolean }>`
  min-height: 100vh;
  background: #f8fafc;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  position: relative;
  overflow-x: hidden;
  width: 100%;
  padding-top: 140px;
  
  @media (max-width: 768px) {
    padding-top: 120px;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const Breadcrumbs = styled(motion.div)<{ $isRTL: boolean }>`
  padding: 1rem 0;
  font-size: 0.9rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};

  @media (max-width: 768px) {
    font-size: 0.8rem;
    margin-bottom: 1.5rem;
  }
`;

const BreadcrumbLink = styled.span<{ $isLast: boolean }>`
  color: ${props => props.$isLast ? '#dc2626' : '#64748b'};
  text-decoration: none;
  font-weight: ${props => props.$isLast ? '600' : '400'};
  cursor: ${props => props.$isLast ? 'default' : 'pointer'};

  &:hover {
    text-decoration: ${props => props.$isLast ? 'none' : 'underline'};
  }
`;

const BreadcrumbSeparator = styled.span`
  color: #94a3b8;
  margin: 0 0.5rem;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
    align-items: flex-start;
  }
`;

const CategoryTitle = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Separator = styled.div`
  height: 1px;
  background: #e2e8f0;
  margin-bottom: 2rem;
`;

const ProductsSection = styled.div`
  margin-bottom: 3rem;
`;

const ProductCount = styled.div`
  font-size: 0.9rem;
  color: #64748b;
  margin-bottom: 2rem;
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-bottom: 3rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const ProductCard = styled(motion.div)<{ $isRTL: boolean }>`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  border: 1px solid #f1f5f9;
  position: relative;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};

  &:hover {
    box-shadow: 0 10px 25px -3px rgba(0, 0, 0, 0.1);
    transform: translateY(-4px);
    border-color: #e2e8f0;
  }

  @media (max-width: 768px) {
    border-radius: 12px;
    
    &:hover {
      transform: translateY(-2px);
    }
  }
`;

const ProductImage = styled.div`
  height: 200px;
  width: 100%;
  overflow: hidden;
  position: relative;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: transform 0.3s ease;
    border-radius: 8px;
  }
  
  ${ProductCard}:hover & img {
    transform: scale(1.05);
  }
  
  @media (max-width: 768px) {
    height: 180px;
    padding: 0.75rem;
  }
`;

const ProductContent = styled.div<{ $isRTL: boolean }>`
  padding: 1.5rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ProductCategory = styled.div`
  font-size: 0.8rem;
  color: #94a3b8;
  text-transform: uppercase;
  font-weight: 500;
  margin-bottom: 0.5rem;
  letter-spacing: 0.5px;
`;

const ProductName = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  line-height: 1.3;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem;
  background: white;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const LoadingText = styled.p`
  color: #64748b;
  font-size: 1.1rem;
  font-weight: 500;
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 4rem;
  background: white;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  text-align: center;
`;

const EmptyStateIcon = styled.div`
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  font-size: 2rem;
`;

const EmptyStateText = styled.p`
  color: #64748b;
  font-size: 1.1rem;
  font-weight: 500;
  margin: 0;
`;

const MaterielElectriqueSubcategory: React.FC<MaterielElectriqueSubcategoryProps> = ({ t, isRTL }) => {
  const navigate = useNavigate();
  const { subcategoryId } = useParams<{ subcategoryId: string }>();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const [products, setProducts] = useState<DatabaseProduct[]>([]);
  const [loading, setLoading] = useState(true);

  // Subcategory mapping
  const subcategoryMap: { [key: string]: string } = {
    'solutions': 'Solutions',
    'isolateurs-composites': 'Isolateurs Composites HTA / HTB',
    'accessoires-chaines': 'Accessoires pour Chaînes d\'Isolateurs',
    'interrupteur-aerien': 'Interrupteur Aérien à Commande Manuelle IACM',
    'accessoires-reseaux-bt': 'Accessoires pour Réseaux B.T',
    'accessoires-protection': 'Accessoires de protection et raccordement',
    'ferrure-armement': 'Ferrure et Armement',
    'eclairage-public': 'Eclairage Public'
  };

  const subcategoryName = subcategoryMap[subcategoryId || ''] || t.subcategory;

  const breadcrumbs = [
    { label: t.catalogTitle, path: "/catalogue" },
    { label: t.electricalEquipmentTitle, path: "/category/materiel-electrique" },
    { label: subcategoryName, path: "" }
  ];

  // Load products for this subcategory
  useEffect(() => {
    const loadProducts = async () => {
        console.log("MaterielElectriqueSubcategory: Loading products for subcategory:", subcategoryId);
      try {
        setLoading(true);
        const allProducts = await FirebaseService.getAll('products');
        
        // Filter for this specific subcategory
        const subcategoryProducts = (allProducts || [])
          .filter((product: DatabaseProduct) => 
            (product.category === '2' || 
             product.category === 'materiel-electrique' ||
             product.category === 'Matériel Électrique') &&
            product.subcategory === subcategoryId
          )
          .sort((a: DatabaseProduct, b: DatabaseProduct) => {
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
            return dateA.getTime() - dateB.getTime();
          });
        
        setProducts(subcategoryProducts);
        console.log("MaterielElectriqueSubcategory: Found products:", subcategoryProducts.length, subcategoryProducts);
      } catch (error) {
        console.error('Error loading subcategory products:', error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    if (subcategoryId) {
      loadProducts();
    }
  }, [subcategoryId]);

  const handleBreadcrumbClick = (path: string) => {
    if (path) {
      navigate(path);
    }
  };

  const handleProductClick = (productId: string) => {
    navigate(`/product/${productId}`);
  };

  const renderProducts = () => {
    if (loading) {
      return (
        <LoadingContainer>
          <LoadingText>{t.loadingProducts}</LoadingText>
        </LoadingContainer>
      );
    }

    if (products.length === 0) {
      return (
        <EmptyStateContainer>
          <EmptyStateIcon>⚡</EmptyStateIcon>
          <EmptyStateText>Aucun produit disponible dans cette sous-catégorie</EmptyStateText>
        </EmptyStateContainer>
      );
    }

    return (
      <ProductsGrid>
        {products.map((product, index) => (
          <ProductCard
            key={product.id}
            $isRTL={isRTL}
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0 }
            }}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            onClick={() => handleProductClick(product.id)}
          >
            <ProductImage>
              <img
                src={product.mainImage || product.images?.[0] || '/images/products/default-product.jpg'}
                alt={product.name || product.title || subcategoryName}
                loading="lazy"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = '/images/products/default-product.jpg';
                }}
              />
            </ProductImage>
            <ProductContent $isRTL={isRTL}>
              <ProductCategory>{subcategoryName.toUpperCase()}</ProductCategory>
              <ProductName>{product.name || product.title || 'Produit'}</ProductName>
            </ProductContent>
          </ProductCard>
        ))}
      </ProductsGrid>
    );
  };

  return (
    <CategoryPage $isRTL={isRTL}>
      <Container>
        <Breadcrumbs
          $isRTL={isRTL}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {breadcrumbs.map((breadcrumb, index) => (
            <React.Fragment key={index}>
              <BreadcrumbLink
                $isLast={index === breadcrumbs.length - 1}
                onClick={() => handleBreadcrumbClick(breadcrumb.path)}
              >
                {breadcrumb.label}
              </BreadcrumbLink>
              {index < breadcrumbs.length - 1 && <BreadcrumbSeparator>›</BreadcrumbSeparator>}
            </React.Fragment>
          ))}
        </Breadcrumbs>

        <HeaderSection>
          <CategoryTitle>{subcategoryName}</CategoryTitle>
        </HeaderSection>

        <Separator />

        <ProductsSection ref={ref}>
          <ProductCount>
            {loading ? t.loading : `${products.length} ${products.length !== 1 ? t.productsPlural : t.productSingular}`}
          </ProductCount>
          
          {renderProducts()}
        </ProductsSection>
      </Container>
    </CategoryPage>
  );
};

export default MaterielElectriqueSubcategory;